import { CheckCircleOutlined } from '@ant-design/icons';
import React from 'react';
import { Button, Flex, theme, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';

import {
  useSelector,
  usePrerenderUserId,
  useHasTrackingCode,
  useIsSubscriptionCancelled,
} from '../../../hooks/usePrerenderUser';
import { ModalTemplate } from './ModalTemplate';
import { useTrackBillingEvent, useSuggestedPlan } from '~features/billing';
import { useTrial } from '../useTrial';
import { useLazyGetTrialPerformanceQuery } from '../FreeTrialApi';
import useTrackTrialEvent from '../useTrackTrialEvent';
import { EmailTemplates } from '~features/CustomerSupport';

export const TrialEndedModal = () => {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const trackBillingEvent = useTrackBillingEvent();
  const { freeTrialState, hasTrialEnded, updateFreeTrialState, trialEndsAt } = useTrial();
  const trackTrialEvent = useTrackTrialEvent();
  const suggestedPlan = useSuggestedPlan();
  const pagesCached = useSelector((state) => state.prerenderUser.numPagesCached);
  const signupDate = useSelector((state) => state.prerenderUser.signupDate);
  const userId = usePrerenderUserId();
  const hasTrackingCode = useHasTrackingCode();
  const isSubscriptionCancelled = useIsSubscriptionCancelled();

  const [fetchMetrics, metricsResult] = useLazyGetTrialPerformanceQuery();

  const signedUpAfterTrialEnded = trialEndsAt && signupDate && signupDate > trialEndsAt;
  const open = hasTrialEnded && !freeTrialState.user.trialEndedModalClosed && !signedUpAfterTrialEnded;

  const trialEndedNotIntegratedLink = EmailTemplates.buildTrialEndedNotIntegrated({ accountId: userId });

  const closeAndBrowsePlans = (ctaName: string) => {
    trackBillingEvent('UpgradeClicked', { cta_location: 'Trial Ended Modal', cta_name: ctaName });

    updateFreeTrialState({ state: { trialEndedModalClosed: true }, scope: 'user' });

    if (suggestedPlan) navigate(`/billing/plans?selectedPlan=${suggestedPlan.id}`);
    else navigate(`/billing/plans`);
  };
  const onUpgrade = () => closeAndBrowsePlans('Stay optimized with a better plan');
  const onBrowsePlans = () => closeAndBrowsePlans('Browse Plans');

  const onDoNotUpgrade = () => {
    trackTrialEvent('BannerClosed', { banner_name: 'TrialEnded' });
    if (!freeTrialState.company.unconvertedFlowEnded)
      updateFreeTrialState({ state: { trialEndedModalClosed: true, displayChurnModal: true }, scope: 'user' });
    else updateFreeTrialState({ state: { trialEndedModalClosed: true }, scope: 'user' });
  };

  const onCloseModal = () => updateFreeTrialState({ state: { trialEndedModalClosed: true }, scope: 'user' });

  const { originalDeliveryTime, averageDeliveryTime, apiRequests } = metricsResult.data
    ? metricsResult.data
    : {
      originalDeliveryTime: 0,
      averageDeliveryTime: 0,
      apiRequests: 0,
    };
  const deliveryTimeImprovement =
    originalDeliveryTime && averageDeliveryTime
      ? Math.round(((originalDeliveryTime - averageDeliveryTime) / originalDeliveryTime) * 100)
      : 0;

  React.useEffect(() => {
    if (open) {
      trackTrialEvent('BannerShown', { banner_name: 'TrialEnded' });
      if (hasTrackingCode) fetchMetrics();
    }
  }, [open, hasTrackingCode]);

  return (
    <ModalTemplate
      open={open}
      footer={
        isSubscriptionCancelled
          ? [
            <Button key="doNotUpgrade" type="text" onClick={onDoNotUpgrade}>
              Don&apos;t upgrade and lose access
            </Button>,
            <Button key="stayOptimized" type="primary" onClick={onUpgrade}>
              Stay optimized and upgrade
            </Button>,
          ]
          : [
            <Button key="gotIt" type="default" onClick={onCloseModal}>
              Got it
            </Button>,
          ]
      }
      title="Your free Starter trial has ended! Let’s see what you’ve achieved:"
      closable={false}
      loading={metricsResult.isLoading}
    >
      <div>
        {hasTrackingCode ? (
          <>
            <Flex vertical gap={10} style={{ marginTop: token.marginLG, marginBottom: token.marginLG }}>
              <Flex align="start" gap={12}>
                <CheckCircleOutlined style={{ fontSize: token.fontSizeHeading3, color: token.colorPrimary }} />
                <div>
                  <Typography.Paragraph strong style={{ margin: 0 }}>
                    {pagesCached} pages cached
                  </Typography.Paragraph>
                  <Typography.Paragraph style={{ margin: 0, fontSize: token.fontSizeSM }}>
                    More pages are now visible to crawlers and your customers.
                  </Typography.Paragraph>
                </div>
              </Flex>
              {!!deliveryTimeImprovement && deliveryTimeImprovement > 0 && (
                <Flex align="start" gap={12}>
                  <CheckCircleOutlined style={{ fontSize: token.fontSizeHeading3, color: token.colorPrimary }} />
                  <div>
                    <Typography.Paragraph strong style={{ margin: 0 }}>
                      {deliveryTimeImprovement}% improvement in delivery time
                    </Typography.Paragraph>
                    <Typography.Paragraph style={{ margin: 0, fontSize: token.fontSizeSM }}>
                      Users are seeing your site sooner in search results.
                    </Typography.Paragraph>
                  </div>
                </Flex>
              )}
              {!!apiRequests && (
                <Flex align="start" gap={12}>
                  <CheckCircleOutlined style={{ fontSize: token.fontSizeHeading3, color: token.colorPrimary }} />
                  <div>
                    <Typography.Paragraph strong style={{ margin: 0 }}>
                      You made {apiRequests} API calls
                    </Typography.Paragraph>
                    <Typography.Paragraph style={{ margin: 0, fontSize: token.fontSizeSM }}>
                      You’ve saved up to 60% on extra developer costs. *API access is part of the Advanced or Custom
                      plans.
                    </Typography.Paragraph>
                  </div>
                </Flex>
              )}
            </Flex>
            {isSubscriptionCancelled && (
              <div style={{ marginBottom: token.marginXL }}>
                <Typography.Paragraph>
                  Pro-tip: check your link previews, organic traffic, and sales data to see extra benefits. Want to keep
                  building on this momentum? Upgrade now to keep these gains rolling.{' '}
                  <Typography.Link onClick={onBrowsePlans}>Browse Plans</Typography.Link>
                </Typography.Paragraph>
                <Typography.Text>⚠️ Heads up: without upgrading, your SEO may take a dip.</Typography.Text>
              </div>
            )}
          </>
        ) : (
          <Typography.Paragraph>
            Oh oh, it look like you never connected your website with Prerender. Want another chance? Contact our
            Support team and ask for another trial at{' '}
            <Typography.Link href={trialEndedNotIntegratedLink}>support@prerender.io</Typography.Link>.
          </Typography.Paragraph>
        )}
      </div>
    </ModalTemplate>
  );
};
